import { useState } from "react";
import { Link } from "react-router-dom";
import { Medal, Award, FileBadge, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel, Initials, Tag } from "@/components/kit";
import { useStore } from "@/store/AppStore";
import { monthTop, yearRanking, yearClosed, YEARS, CURRENT_YEAR } from "@/lib/highlights";
import { MONTH_LABEL } from "@/data/mock";

const MEDAL = { 1: "bg-gold text-neutral-900", 2: "bg-neutral-900 text-gold", 3: "bg-gold-soft text-gold-dark" };

const CertLink = ({ to, testId }) => (
  <Button asChild size="sm" variant="outline" className="mt-4 w-full" data-testid={testId}>
    <Link to={to}><FileBadge className="mr-1 h-4 w-4 text-gold-dark" /> Certificado</Link>
  </Button>
);

function TopCard({ r, cert, testId, hint, hideScore }) {
  return (
    <div data-testid={testId} className={`relative flex flex-col items-center rounded-xl border p-5 text-center ${r.position === 1 ? "border-gold bg-gold-soft/40" : "border-neutral-200"}`}>
      <span className={`absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${MEDAL[r.position]}`}>{r.position}º</span>
      <Initials name={r.name} className="h-14 w-14 text-base ring-2 ring-gold" />
      <p className="mt-3 text-sm font-semibold">{r.name}</p>
      <p className="text-xs text-neutral-500">{r.role}</p>
      {!hideScore && <p className="mt-2 font-display text-2xl font-semibold">{r.score}<span className="text-xs text-neutral-400"> pts</span></p>}
      {hint && <p className="text-[11px] text-neutral-400">{hint}</p>}
      {cert && <CertLink to={cert} testId={`${testId}-cert`} />}
    </div>
  );
}

export function MonthHighlights({ companyId, certificates = true, className = "" }) {
  const { state } = useStore();
  const top = monthTop(state, companyId);
  return (
    <Panel title="Destaques do mês" subtitle={`${MONTH_LABEL} · 1º, 2º e 3º lugar do ranking`} className={className} testId="month-highlights" action={<Medal className="h-5 w-5 text-gold" />}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {top.map((r) => <TopCard key={r.id} r={r} hideScore={!certificates} testId={`month-top-${r.position}`} cert={certificates && `/certificado?tipo=mes&empresa=${companyId}&colab=${r.id}`} />)}
      </div>
    </Panel>
  );
}

export function YearHighlights({ companyId }) {
  const { state } = useStore();
  const [year, setYear] = useState(CURRENT_YEAR - 1);
  const closed = yearClosed(year);
  const top = yearRanking(state, companyId, year).slice(0, 3);
  return (
    <Panel title="Destaque do ano" subtitle="Média da pontuação mensal no ano" testId="year-highlights" action={
      <div className="flex gap-1 rounded-lg bg-neutral-100 p-1">
        {YEARS.map((y) => <button key={y} onClick={() => setYear(y)} data-testid={`year-tab-${y}`} className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${y === year ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500"}`}>{y}</button>)}
      </div>
    }>
      {closed ? <p className="mb-4 flex items-center gap-2 text-xs text-neutral-500"><Award className="h-4 w-4 text-gold" /> Ano encerrado. Certificados de Destaque do Ano liberados.</p> : (
        <p className="mb-4 flex items-center gap-2 text-xs text-neutral-500" data-testid="year-locked"><Lock className="h-4 w-4 text-gold" /> Classificação parcial. <Tag tone="gold">Certificado liberado ao finalizar {year}</Tag></p>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {top.map((r) => <TopCard key={r.id} r={r} testId={`year-top-${r.position}`} hint={`média de ${r.months} ${r.months === 1 ? "mês" : "meses"}`} cert={closed && `/certificado?tipo=ano&empresa=${companyId}&colab=${r.id}&ano=${year}`} />)}
      </div>
    </Panel>
  );
}
